import { useCallback, useMemo } from 'react'

import { faComment } from '@fortawesome/pro-regular-svg-icons'
import { Button, Collapse, CollapseProps, Flex, Skeleton } from 'antd'
import { Icon, Modal } from 'govwell-ui'
import styled from 'styled-components'
import { AIChatbotFAQFragment } from 'types/graphql'

import { Label } from 'src/components/form/Label'
import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import Text from 'src/components/Typography/Text'
import {
  useAnswerAIChatbotFAQMutation,
  useGetAIChatbotFAQWithSessionsQuery,
} from 'src/fetch/aiChatbotFAQ'
import { useAppForm } from 'src/hooks/use-app-form'
import useDisclosure from 'src/hooks/use-disclosure'
import AISessionDisplay from 'src/pages/AIAssistantHistoryPage/AISessionDisplay'
import { RefetchFnType } from 'src/types'
import { formatUserName } from 'src/utils'
import { DateFormats } from 'src/utils/date'
import { formatDate } from 'src/utils/date'

type Props = {
  aiChatbotFAQ: AIChatbotFAQFragment
  refetch: RefetchFnType
}

const CollapseContainer = styled.div`
  .ant-collapse-header {
    align-items: center !important;
  }
`

const AnswerQuestionButton = ({ aiChatbotFAQ, refetch }: Props) => {
  const modalState = useDisclosure()
  const {
    data,
    isLoading,
    refetch: refetchSessions,
  } = useGetAIChatbotFAQWithSessionsQuery(aiChatbotFAQ.id, modalState.isOpen)
  const sessions = useMemo(() => data?.aiChatbotFAQ.mostRecentConnectedSessions || [], [data])

  const collapseItems: CollapseProps['items'] = useMemo(() => {
    return sessions.map((session) => {
      const time = formatDate(session.startedAt, DateFormats.MonthDayYear)
      const label = session.user
        ? `Session with ${formatUserName(session.user)} on ${time}`
        : `Session with anonymous user on ${time}`
      return {
        label: <Flex align="center">{label}</Flex>,
        key: session.id,
        children: <AISessionDisplay session={session} refetch={refetchSessions} />,
      }
    })
  }, [sessions, refetchSessions])

  const defaultActiveKey: CollapseProps['defaultActiveKey'] = useMemo(() => {
    return sessions.length > 0 ? sessions[0]?.id : undefined
  }, [sessions])

  const form = useAppForm({
    defaultValues: {
      answer: '',
    },
  })

  const { mutateAsync: answerAIChatbotFAQ, isPending: isAnswering } =
    useAnswerAIChatbotFAQMutation()

  const { notification } = useNotification()

  const handleClick = useCallback(async () => {
    await answerAIChatbotFAQ({
      id: aiChatbotFAQ.id,
      input: {
        answer: form.getFieldValue('answer'),
      },
    })

    notification.info({
      message: `Great job! Your answer has been added to the Custom Answers.`,
      placement: 'bottomRight',
    })
    await refetch()
    modalState.close()
  }, [answerAIChatbotFAQ, aiChatbotFAQ.id, form, notification, refetch, modalState])

  return (
    <>
      <Button size="small" type="default" icon={<Icon.FaComment />} onClick={modalState.open}>
        Answer + Add to Custom Answers
      </Button>
      {modalState.isOpen && (
        <Modal onClose={modalState.close} width="800px">
          <Modal.Header icon={faComment}>Answer Question and Add to Custom Answers</Modal.Header>
          <Modal.Content>
            {isLoading && <Skeleton active />}
            {!isLoading && (
              <Flex gap="12px">
                <Flex flex={1}>
                  <Flex vertical gap="12px">
                    <Flex vertical>
                      <Label label="Question" />
                      <Text>{aiChatbotFAQ.question}</Text>
                    </Flex>
                    <Flex vertical>
                      <Label label="Times asked" />
                      <Text>{aiChatbotFAQ.count}</Text>
                    </Flex>
                    <form.AppField name="answer">
                      {(field) => (
                        <field.RichTextInput
                          caption="Your answer will be added to the Community Assistant's knowledge base."
                          field={field}
                          isRequired
                          label="Answer"
                        />
                      )}
                    </form.AppField>
                  </Flex>
                </Flex>
                <Flex flex={1} vertical gap="12px">
                  <Label
                    label={
                      collapseItems.length === 1
                        ? 'Most recent session featuring this question'
                        : `${collapseItems.length} most recent sessions featuring this question`
                    }
                  />
                  <CollapseContainer>
                    <Collapse items={collapseItems} accordion defaultActiveKey={defaultActiveKey} />
                  </CollapseContainer>
                </Flex>
              </Flex>
            )}
          </Modal.Content>
          <Modal.Footer>
            <Button type="primary" onClick={handleClick} loading={isAnswering}>
              Save and add to Custom Answers
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default React.memo(AnswerQuestionButton)
